import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Check, TrendingUp, X } from 'lucide-react';
import api from '@/lib/api';

const UpgradePromptContext = createContext(null);

// Resource-specific copy: title, subtitle, and which plans to recommend.
const RESOURCE_COPY = {
  properties: {
    icon: '🏠',
    title: "You've hit your property limit",
    subtitle:
      "Your current plan doesn't allow adding another property. Upgrade to keep growing your portfolio.",
    recommend: ['pro', 'business'],
  },
  staff: {
    icon: '👥',
    title: "You've hit your staff-seat limit",
    subtitle:
      "Your current plan doesn't allow inviting another teammate. Upgrade to add more staff.",
    recommend: ['pro', 'business'],
  },
  generic: {
    icon: '↗',
    title: 'Upgrade required',
    subtitle: 'Your current plan does not support this action.',
    recommend: ['pro', 'business'],
  },
};

const PLAN_META = {
  starter: { name: 'Starter', price: 29, blurb: '3 properties · 1 staff seat' },
  pro:     { name: 'Pro',     price: 79, blurb: '15 properties · 3 staff seats', highlight: true },
  business:{ name: 'Business',price: 199,blurb: 'Unlimited properties & staff' },
};

export const UpgradePromptProvider = ({ children }) => {
  const [state, setState] = useState({ open: false, resource: 'generic', detail: '' });
  const [currentPlan, setCurrentPlan] = useState(null);

  const showUpgradePrompt = useCallback((opts) => {
    setState({
      open: true,
      resource: opts?.resource || 'generic',
      detail: opts?.detail || '',
    });
  }, []);

  const closeUpgradePrompt = useCallback(() => {
    setState((s) => ({ ...s, open: false }));
  }, []);

  // When opening, fetch subscription so we can show current plan
  useEffect(() => {
    if (!state.open) return;
    api.get('/billing/subscription')
      .then((r) => setCurrentPlan(r.data?.subscription_plan || 'beta'))
      .catch(() => setCurrentPlan('beta'));
  }, [state.open]);

  const copy = RESOURCE_COPY[state.resource] || RESOURCE_COPY.generic;

  return (
    <UpgradePromptContext.Provider value={{ showUpgradePrompt, closeUpgradePrompt }}>
      {children}
      <Dialog open={state.open} onOpenChange={(o) => !o && closeUpgradePrompt()}>
        <DialogContent
          className="max-w-lg p-0 overflow-hidden border-0"
          data-testid="upgrade-prompt-dialog"
        >
          {/* Header with navy brand band */}
          <div className="bg-[#070d1c] text-white p-6 relative">
            <div className="absolute top-4 right-4">
              <button
                type="button"
                onClick={closeUpgradePrompt}
                className="p-1 rounded hover:bg-white/10 text-slate-400 hover:text-white transition-colors"
                aria-label="Close"
                data-testid="upgrade-prompt-close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-lg bg-amber-400/15 text-amber-300 ring-1 ring-amber-400/40 flex items-center justify-center flex-shrink-0">
                <TrendingUp className="h-5 w-5" />
              </div>
              <div>
                <DialogHeader className="text-left space-y-1">
                  <DialogTitle className="text-white text-xl font-manrope">
                    {copy.title}
                  </DialogTitle>
                  <DialogDescription className="text-slate-300 leading-relaxed">
                    {copy.subtitle}
                  </DialogDescription>
                </DialogHeader>
                {currentPlan && (
                  <p className="text-xs text-slate-400 mt-3">
                    Current plan:{' '}
                    <span className="text-amber-200 font-medium">
                      {PLAN_META[currentPlan]?.name || 'Beta'}
                    </span>
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Plan options */}
          <div className="p-6 bg-white space-y-3">
            {copy.recommend.map((planId) => {
              const p = PLAN_META[planId];
              return (
                <div
                  key={planId}
                  className={`relative rounded-lg border p-4 flex items-center justify-between transition-colors ${
                    p.highlight
                      ? 'border-amber-400 bg-amber-50/40 shadow-sm'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  {p.highlight && (
                    <span className="absolute -top-2.5 left-4 bg-amber-400 text-[#070d1c] text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full">
                      Recommended
                    </span>
                  )}
                  <div>
                    <div className="flex items-baseline gap-2">
                      <span className="font-manrope font-bold text-lg text-slate-900">
                        {p.name}
                      </span>
                      <span className="text-slate-500 text-sm">
                        ${p.price}/mo
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">{p.blurb}</p>
                  </div>
                  <Link to={`/pricing`} onClick={closeUpgradePrompt}>
                    <Button
                      className={
                        p.highlight
                          ? 'bg-amber-400 hover:bg-amber-300 text-[#070d1c] font-semibold shadow-md shadow-amber-500/20'
                          : ''
                      }
                      data-testid={`upgrade-prompt-pick-${planId}`}
                    >
                      <Check className="h-4 w-4 mr-1.5" />
                      Choose {p.name}
                    </Button>
                  </Link>
                </div>
              );
            })}
          </div>

          <DialogFooter className="bg-slate-50 px-6 py-3 border-t border-slate-200 sm:justify-between">
            <p className="text-xs text-slate-500">
              14-day trial · Cancel anytime
            </p>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={closeUpgradePrompt}
              data-testid="upgrade-prompt-later"
            >
              Maybe later
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </UpgradePromptContext.Provider>
  );
};

export const useUpgradePrompt = () => {
  const ctx = useContext(UpgradePromptContext);
  if (!ctx) {
    // Fallback no-op when Provider isn't mounted (marketing pages)
    return { showUpgradePrompt: () => {}, closeUpgradePrompt: () => {} };
  }
  return ctx;
};

/**
 * Helper: given an axios error, detect a 402 plan-limit response and
 * return the resource type ('properties' | 'staff') or null. Uses the
 * upstream error detail string as the hint.
 */
export const detectPlanLimitError = (error) => {
  const status = error?.response?.status;
  if (status !== 402) return null;
  const detail = String(error?.response?.data?.detail || '').toLowerCase();
  if (detail.includes('propert')) return 'properties';
  if (detail.includes('staff') || detail.includes('seat')) return 'staff';
  return 'generic';
};

export default UpgradePromptProvider;
